import { useState } from "react";
import { SECTION_HEADING_CLASS } from "bizpandit-ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Card, CardContent, ConfirmDialog, Spinner, toast } from "bizpandit-ui";
import type { ApiError } from "@/api/types";
import { accountSettingsService, type ConsentStatus } from "../services/accountSettings.service";

/**
 * A18 — "Client portal shows own consent status and allows withdrawal."
 * Its register note is the requirement in full: "Withdrawal must be as easy as
 * giving it."
 *
 * Before this panel a client could do neither. /consent/* is staff-only, so the
 * only way to withdraw was to email and ask someone to do it for you — which is
 * precisely the asymmetry DPDP §6 forbids.
 *
 * THREE STATES, and the first one is the one everybody is in today. All 34
 * contacts on production carry consent_source = 'PRE_POLICY' with
 * consent_given = FALSE: the A16 decision, which recorded that consent predating
 * the policy is not evidence of consent under it. So the common case is "we have
 * not recorded your consent", and the panel has to say that plainly rather than
 * imply a permission nobody gave.
 *
 * There is no "give consent" button, and its absence is deliberate rather than
 * an oversight: ConsentService.grant refuses a version that does not name a
 * published PrivacyNotice, and that table is empty until the notice is
 * published (K1). A button that always errors is worse than none on the screen
 * where a person comes to exercise a right.
 */
function stateOf(c: ConsentStatus): "WITHDRAWN" | "GIVEN" | "NOT_RECORDED" {
  if (c.consent_withdrawn) return "WITHDRAWN";
  if (c.consent_given) return "GIVEN";
  return "NOT_RECORDED";
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function ConsentPanel() {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: consent, isLoading, isError } = useQuery({
    queryKey: ["portal", "consent"],
    queryFn: () => accountSettingsService.consent(),
    retry: false,
  });

  const withdraw = useMutation({
    mutationFn: (reason?: string) => accountSettingsService.withdrawConsent(reason),
    onSuccess: (updated) => {
      queryClient.setQueryData(["portal", "consent"], updated);
      toast.success("Consent withdrawn. We have stopped contacting you.");
    },
    onError: (error: ApiError) =>
      toast.error(error.message ?? "We couldn't record that. Please try again."),
  });

  if (isLoading) {
    return (
      <Card className="border border-linegray rounded-[14px] mb-4">
        <CardContent className="p-4 flex items-center gap-2 text-[13px] text-slate-500">
          <Spinner /> Loading your consent…
        </CardContent>
      </Card>
    );
  }

  // A client account with no linked contact record 403s. Saying nothing at all
  // is better than showing a consent panel that cannot describe this person.
  if (isError || !consent) return null;

  const state = stateOf(consent);

  return (
    <Card className="border border-linegray rounded-[14px] mb-4">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className={`m-0 ${SECTION_HEADING_CLASS}`}>Privacy &amp; consent</h2>
          {state === "WITHDRAWN" && <Badge variant="destructive">Withdrawn</Badge>}
          {state === "GIVEN" && <Badge variant="success">Given</Badge>}
          {state === "NOT_RECORDED" && <Badge variant="secondary">Not recorded</Badge>}
        </div>

        {state === "GIVEN" && (
          <>
            <dl className="grid grid-cols-[130px_1fr] gap-y-2 text-[13px] m-0 mb-3">
              <dt className="text-slate-500">Given on</dt>
              <dd className="m-0 text-navy2">{formatDate(consent.consent_date)}</dd>
              <dt className="text-slate-500">Notice version</dt>
              <dd className="m-0 text-navy2">{consent.consent_version ?? "—"}</dd>
              <dt className="text-slate-500">WhatsApp updates</dt>
              <dd className="m-0 text-navy2">
                {consent.whatsapp_opt_in ? "On" : "Off"}
              </dd>
            </dl>
            <p className="text-[13px] text-slate-500 mb-3 leading-relaxed">
              You can withdraw this at any time. We will stop contacting you and
              stop processing your data on the basis of your consent.
            </p>
          </>
        )}

        {state === "NOT_RECORDED" && (
          <p className="text-[13px] text-slate-500 mb-3 leading-relaxed">
            We have not recorded a consent from you under our current privacy
            policy. Your account and reports are unaffected. We will ask you to
            confirm your choices when the updated policy is published — until
            then we are not contacting you for marketing.
          </p>
        )}

        {state === "WITHDRAWN" && (
          <>
            <dl className="grid grid-cols-[130px_1fr] gap-y-2 text-[13px] m-0 mb-3">
              <dt className="text-slate-500">Withdrawn on</dt>
              <dd className="m-0 text-navy2">{formatDate(consent.consent_withdrawn_date)}</dd>
            </dl>
            <p className="text-[13px] text-slate-500 mb-0 leading-relaxed">
              We have stopped contacting you, and any links we had shared to your
              reports have been revoked.{" "}
              <strong className="text-navy2">
                Your reports and charts have not been deleted
              </strong>{" "}
              — withdrawing consent is not the same as erasing your data. To
              start again, or to erase everything, contact your consultant.
            </p>
          </>
        )}

        {state !== "WITHDRAWN" && (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="border border-linegray bg-white text-navy2 px-3 py-1.5 rounded-[10px] text-sm hover:bg-surface transition-colors"
          >
            Withdraw my consent
          </button>
        )}

        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onConfirm={(reason) => {
            withdraw.mutate(reason);
            setConfirmOpen(false);
          }}
          tone="danger"
          title="Withdraw your consent?"
          description="We will stop contacting you, switch off WhatsApp updates, and revoke any report links we have shared."
          warning="This does not delete your data. Your reports and charts stay exactly as they are."
          confirmLabel="Withdraw consent"
          reason={{
            label: "Reason (optional)",
            placeholder: "Helps us understand — you do not have to give one.",
          }}
          busy={withdraw.isPending}
        />
      </CardContent>
    </Card>
  );
}
